import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useMotionValue, type MotionValue } from 'motion/react';
import { mapLinear } from '../../_lib/mapLinear';
import { getScrollContent, getScrollWrapper } from '../../LenisScroll';

/**
 * A custom hook that tracks the frame rectangle and progress of a specified element within a scrollable container.
 *
 * This hook takes in a `track` element and optional `margin` settings as parameters and returns a `ProgressTracker` object.
 * The `ProgressTracker` object contains information about the frame rectangle and observer for the specified element.
 *
 * The hook utilizes the `useRef` and `useFrame` hooks from React, as well as `useMotionValue` from motion.
 * It also relies on helper functions for calculating offsets, viewport visibility, and progress values.
 *
 * The `useFrameRectAndProgressTracker` hook continuously updates the frame rectangle and progress values based on the scroll position and dimensions of the specified element.
 * It also updates the motion-based progress values and visibility flags for the specified element.
 *
 * The hook provides access to the frame rectangle, progress values, and observer flags for the specified element, allowing for interactive and motion-based effects.
 *
 * @param track The element to track within a scrollable container.
 * @param margin Optional margin settings for calculating visibility within the scrollable container. Defaults to `{ top: 0, right: 0, bottom: 0, left: 0 }`.
 * @param sceneAspectRatio Optional aspect ratio settings for calculating the frame rectangle. Defaults to `{ width: 16, height: 9 }`.
 * A value of 1 is equivalent to the viewport height (width for horizontal values). Positive values increase the frame rectangle, negative values decrease.
 * @returns A `ProgressTracker` object containing the frame rectangle and observer for the specified element.
 */

function useFrameRectAndProgressTracker(
	track?: HTMLElement | null,
	margin?: {
		top?: number;
		right?: number;
		bottom?: number;
		left?: number;
	},
	sceneAspectRatio?: {
		width: number;
		height: number;
	},
	scrollPositioning?: boolean,
): RectAndProgressTracker {
	const resizeRect = useRef({
		bodyWidth: 0,
		bodyHeight: 0,
		windowWidth: 0,
		windowHeight: 0,
		rectNeedsUpdate: false,
	}).current;

	const rect = useRef<FrameRect>({
		hasBeenUpdatedAtLeastOnce: false,
		left: 0,
		top: 0,
		bottom: 0,
		width: 0,
		height: 0,
		threeJsRects: {
			x: 0,
			y: 0,
			sceneScale: 1,
			sceneAspectRatio: { width: sceneAspectRatio?.width || 16, height: sceneAspectRatio?.height || 9 },
		},
		computedStyle: {
			paddingTop: 0,
			paddingRight: 0,
			paddingBottom: 0,
			paddingLeft: 0,
		},
		topOffset: 0,
		leftOffset: 0,
		windowWidth: 0,
		windowHeight: 0,
		maxScrollXDistance: 0,
		maxScrollYDistance: 0,
		wrapper: null,
		content: null,
	}).current;

	const observer = useRef<FrameObserver>({
		positionX: 0,
		positionY: 0,
		progressX: 0,
		progressY: 0,
		motionProgressX: useMotionValue(0),
		motionProgressY: useMotionValue(0),
		inViewX: false,
		inViewY: false,
		inViewXWithMargin: false,
		inViewYWithMargin: false,
	}).current;

	function setRect() {
		const _rect = track?.getBoundingClientRect();
		if (_rect && track) {
			rect.left = _rect.left;
			rect.top = _rect.top;
			rect.bottom = _rect.bottom;
			rect.width = track.clientWidth;
			rect.height = track.clientHeight;

			rect.topOffset = getTopOffset(track);
			rect.leftOffset = getLeftOffset(track);
			const computedStyle = getParentComputedStyle(track);
			if (computedStyle) rect.computedStyle = computedStyle;
		}

		if (rect.wrapper && rect.content) {
			rect.maxScrollXDistance = rect.content.clientWidth - rect.wrapper.clientWidth;
			rect.maxScrollYDistance = rect.content.clientHeight - rect.wrapper.clientHeight;
			rect.windowWidth = rect.wrapper.clientWidth;
			rect.windowHeight = rect.wrapper.clientHeight;

			if (_rect && track) {
				// move coordinate system so 0,0 is at center of screen
				if (track.style.position === 'fixed' || !scrollPositioning) {
					rect.threeJsRects.x = rect.left + track.clientWidth * 0.5 - rect.wrapper.clientWidth * 0.5;
					rect.threeJsRects.y = -rect.top - track.clientHeight * 0.5 + rect.wrapper.clientHeight * 0.5;
				} else {
					rect.threeJsRects.x = rect.leftOffset + track.clientWidth * 0.5 - rect.wrapper.clientWidth * 0.5;
					rect.threeJsRects.y = -rect.topOffset - track.clientHeight * 0.5 + rect.wrapper.clientHeight * 0.5;
				}
			}
		}

		if (!rect.hasBeenUpdatedAtLeastOnce) {
			rect.hasBeenUpdatedAtLeastOnce = true;
		}
	}

	function getProgressY() {
		if (rect.wrapper) {
			const top = rect.topOffset - rect.wrapper.scrollTop;
			const pxInside = rect.wrapper.clientHeight - top;
			const progress = mapLinear(pxInside, 0, rect.wrapper.clientHeight + rect.height, 0, 1); // percent of total visible distance

			return progress;
		}
		return 0;
	}

	function getProgressX() {
		if (rect.wrapper) {
			const left = rect.leftOffset - rect.wrapper.scrollLeft;
			const pxInside = rect.wrapper.clientWidth - left;
			const progress = mapLinear(pxInside, 0, rect.wrapper.clientWidth + rect.width, 0, 1); // percent of total visible distance

			return progress;
		}
		return 0;
	}

	function getInViewWithMarginY(margin: { top?: number; bottom?: number }) {
		if (rect.wrapper) {
			const marginTop = rect.wrapper.clientHeight * (margin.top || 0);
			const marginBottom = rect.wrapper.clientHeight * (margin.bottom || 0);

			const top = rect.topOffset - rect.wrapper.scrollTop + marginTop * -1;
			const pxInside = rect.wrapper.clientHeight - top;
			const progress = mapLinear(pxInside, 0, rect.wrapper.clientHeight + (marginTop + marginBottom) + rect.height, 0, 1); // percent of total visible distance

			if (progress < 1 && progress > 0) {
				return true;
			}
		}

		return false;
	}

	function getInViewWithMarginX(margin: { left?: number; right?: number }) {
		if (rect.wrapper) {
			const marginLeft = rect.wrapper.clientHeight * (margin.left || 0);
			const marginRight = rect.wrapper.clientHeight * (margin.right || 0);

			const left = rect.leftOffset - rect.wrapper.scrollLeft + marginLeft * -1;
			const pxInside = rect.wrapper.clientWidth - left;
			const progress = mapLinear(pxInside, 0, rect.wrapper.clientWidth + (marginLeft + marginRight) + rect.width, 0, 1); // percent of total visible distance

			if (progress < 1 && progress > 0) {
				return true;
			}
		}

		return false;
	}

	useFrame(() => {
		if (rect.wrapper === null) rect.wrapper = getScrollWrapper();
		if (rect.content === null) rect.content = getScrollContent();

		if (rect.content && rect.wrapper) {
			if (
				rect.content.clientWidth !== resizeRect.bodyWidth ||
				rect.content.clientHeight !== resizeRect.bodyHeight ||
				rect.wrapper.clientWidth !== resizeRect.windowWidth ||
				rect.wrapper.clientHeight !== resizeRect.windowHeight
			) {
				resizeRect.bodyHeight = rect.content.clientHeight;
				resizeRect.bodyWidth = rect.content.clientWidth;
				resizeRect.windowHeight = rect.wrapper.clientHeight;
				resizeRect.windowWidth = rect.wrapper.clientWidth;
				resizeRect.rectNeedsUpdate = true;
			}

			if (track) {
				if (
					track.clientWidth !== rect.width ||
					track.clientHeight !== rect.height ||
					getTopOffset(track) !== rect.topOffset ||
					getLeftOffset(track) !== rect.leftOffset
				) {
					resizeRect.rectNeedsUpdate = true;
				}
			}

			if (resizeRect.rectNeedsUpdate) {
				setRect();

				if (track) {
					if (
						sceneAspectRatio &&
						(sceneAspectRatio.width !== rect.threeJsRects.sceneAspectRatio.width ||
							sceneAspectRatio.height !== rect.threeJsRects.sceneAspectRatio.height)
					) {
						rect.threeJsRects.sceneAspectRatio.width = sceneAspectRatio.width;
						rect.threeJsRects.sceneAspectRatio.height = sceneAspectRatio.height;
					}

					const wrapperAspectRatio = track.clientWidth / track.clientHeight;
					const sceneAspectRatioValue = (sceneAspectRatio?.width || 16) / (sceneAspectRatio?.height || 9);

					if (wrapperAspectRatio <= sceneAspectRatioValue) {
						rect.threeJsRects.sceneScale = track.clientWidth / sceneAspectRatioValue / 100;
					} else {
						rect.threeJsRects.sceneScale = track.clientHeight / 100;
					}
				}
				resizeRect.rectNeedsUpdate = false;
			}

			if (rect.hasBeenUpdatedAtLeastOnce) {
				if (track?.style.position === 'fixed' || !scrollPositioning) {
					const positionX = rect.threeJsRects.x;
					if (positionX !== observer.positionX) observer.positionX = positionX;

					const positionY = rect.threeJsRects.y;
					if (positionY !== observer.positionY) observer.positionY = positionY;
				} else {
					const positionX = rect.threeJsRects.x + rect.wrapper.scrollLeft;
					if (positionX !== observer.positionX) observer.positionX = positionX;

					const positionY = rect.threeJsRects.y + rect.wrapper.scrollTop;
					if (positionY !== observer.positionY) observer.positionY = positionY;
				}

				const progressY = getProgressY();

				if (progressY !== observer.progressY) {
					observer.progressY = progressY;
					observer.motionProgressY.set(progressY);
				}
				if (progressY < 1 && progressY > 0 && !observer.inViewY) observer.inViewY = true;
				if (observer.inViewY && !(progressY < 1 && progressY > 0)) observer.inViewY = false;

				const inViewYWithMargin = getInViewWithMarginY({ top: margin?.top, bottom: margin?.bottom });
				if (observer.inViewYWithMargin !== inViewYWithMargin) {
					observer.inViewYWithMargin = inViewYWithMargin;
				}

				const inViewXWithMargin = getInViewWithMarginX({ left: margin?.left, right: margin?.right });
				if (observer.inViewXWithMargin !== inViewXWithMargin) {
					observer.inViewXWithMargin = inViewXWithMargin;
				}

				const progressX = getProgressX();
				if (progressX !== observer.progressX) {
					observer.progressX = progressX;
					observer.motionProgressX.set(progressX);
				}
				if (progressX < 1 && progressX > 0 && !observer.inViewX) observer.inViewX = true;
				if (observer.inViewX && !(progressX < 1 && progressX > 0)) observer.inViewX = false;
			}
		}
	});

	return { rect, observer };
}

function getTopOffset(element: HTMLElement | null) {
	if (!element) return 0;

	let coords = 0;
	while (element) {
		coords += element.offsetTop;
		element = element.offsetParent as HTMLElement;
	}
	return coords;
}

function getLeftOffset(element: HTMLElement | null) {
	if (!element) return 0;

	let coords = 0;
	while (element) {
		coords += element.offsetLeft;
		element = element.offsetParent as HTMLElement;
	}
	return coords;
}

function getParentComputedStyle(element: HTMLElement) {
	const styles = getComputedStyle(element);
	const paddingTop = parseInt(styles.paddingTop.replace('px', ''), 10);
	const paddingRight = parseInt(styles.paddingRight.replace('px', ''), 10);
	const paddingBottom = parseInt(styles.paddingBottom.replace('px', ''), 10);
	const paddingLeft = parseInt(styles.paddingLeft.replace('px', ''), 10);

	return {
		paddingTop,
		paddingRight,
		paddingBottom,
		paddingLeft,
	};
}

export { useFrameRectAndProgressTracker };

/**
 * Represents an object that observes the frame rectangle of an element within a scrollable container.
 */
export type FrameObserver = {
	positionX: number; // The horizontal position of the element within the scrollable container.
	positionY: number; // The vertical position of the element within the scrollable container.
	progressX: number; // The horizontal progress of the element within the scrollable container.
	progressY: number; // The vertical progress of the element within the scrollable container.
	motionProgressX: MotionValue; // A motion value representing the horizontal progress for animation purposes.
	motionProgressY: MotionValue; // A motion value representing the vertical progress for animation purposes.
	inViewX: boolean; // Indicates whether the element is currently in view horizontally within the scrollable container.
	inViewY: boolean; // Indicates whether the element is currently in view vertically within the scrollable container.
	inViewXWithMargin: boolean; // Indicates whether the element is in view horizontally within the scrollable container with the specified margin.
	inViewYWithMargin: boolean; // Indicates whether the element is in view vertically within the scrollable container with the specified margin.
};

/**
 * Represents the frame rectangle of an element within a scrollable container.
 */
export type FrameRect = {
	hasBeenUpdatedAtLeastOnce: boolean; // Indicates whether the frame rectangle has been updated at least once.
	left: number; // The left coordinate of the frame rectangle.
	top: number; // The top coordinate of the frame rectangle.
	bottom: number; // The bottom coordinate of the frame rectangle.
	width: number; // The width of the frame rectangle.
	height: number; // The height of the frame rectangle.
	threeJsRects: {
		x: number; // The x-coordinate of the center of the frame rectangle for three js calculations.
		y: number; // The y-coordinate of the center of the frame rectangle for three js calculations.
		sceneScale: number; // The scene scale multiplier for the frame rectangle for three js calculations.
		sceneAspectRatio: { width: number; height: number }; // The scene aspect ratio for the frame rectangle for three js calculations.
	};
	computedStyle: {
		paddingTop: number; // The padding top value of the element's computed style.
		paddingRight: number; // The padding right value of the element's computed style.
		paddingBottom: number; // The padding bottom value of the element's computed style.
		paddingLeft: number; // The padding left value of the element's computed style.
	};
	topOffset: number; // The top offset of the element within the scrollable container.
	leftOffset: number; // The left offset of the element within the scrollable container.
	windowWidth: number; // The width of the scrollable container (screen width).
	windowHeight: number; // The height of the scrollable container (screen height).
	maxScrollXDistance: number; // The maximum scroll distance in the horizontal direction.
	maxScrollYDistance: number; // The maximum scroll distance in the vertical direction.
	wrapper: HTMLElement | null; // The scrollable container element.
	content: HTMLElement | null; // The scrollable content element.
};

export type RectAndProgressTracker = { rect: FrameRect; observer: FrameObserver };
